import { SharedAlbum } from 'networking/types/shared-album';
import { useContext, useState, useCallback, useMemo } from 'react';
import { VaultNavigationTabs } from 'common/enums';
import { useScopedAlbums } from 'hooks/use-scoped-albums';
import { goToPage, RouteName } from 'routes';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import { VaultDetailContext } from 'context/vault-detail-context';
import { excludeDefaultAlbum } from 'helpers/utils';
import { Album } from 'models/album';
import { useCreateAlbum } from 'pages/vault-detail/hooks/use-create-album';
import { NewAlbumModal } from 'pages/vault-detail/components/vault-top-bar/components/new-album-modal';
import { CollectionCard } from '../dashboard/components/collection-card';
import { NewAlbumCard } from '../dashboard/components/new-album-card';
import { EmptyAlbumsState } from './components/empty-albums-state';
import { EmptyDashboardState } from './components/empty-dashboard-state';
import { filterAlbumsBySearch, sortAlbums } from '../utils/album-list-utils';
import styles from './albums.module.scss';

export const Albums = () => {
  const {
    state: { vaultId, albumsSortBy, searchQuery },
  } = useContext(VaultDetailContext);
  const createAlbum = useCreateAlbum();
  const [showNewAlbumModal, setShowNewAlbumModal] = useState(false);

  // The "My Stuff" / "Shared With Me" switch (see useScopedAlbums). It merges
  // answered and unanswered albums, so empty ones — new ones included — show.
  const scoped = useScopedAlbums();
  const allAlbums = useMemo(
    () =>
      excludeDefaultAlbum(scoped.albums).sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [scoped.albums],
  );

  const filteredAlbums = useMemo(() => {
    const list = filterAlbumsBySearch(allAlbums, searchQuery);

    return sortAlbums(list, albumsSortBy);
  }, [allAlbums, searchQuery, albumsSortBy]);

  // Breadcrumb origin for the album page: this vault's Albums tab.
  const origin = withOrigin({ vaultId, tab: VaultNavigationTabs.albums });

  const handleAlbumClick = useCallback(
    (album: Album) => {
      // Albums shared from other vaults open in their own vault.
      const albumVaultId = scoped.crossVault
        ? (album as SharedAlbum).vaultId
        : vaultId;
      goToPage(
        RouteName.AlbumScreen,
        { id: albumVaultId, albumId: album.id },
        {},
        origin,
      );
    },
    [vaultId, scoped.crossVault],
  );

  // Same flow as the dashboard's "New Album" card: name it in a modal, create
  // it, then open its page.
  const handleCreateNamedAlbum = async (name: string) => {
    setShowNewAlbumModal(false);
    const album = await createAlbum(vaultId, name);
    if (!album) return;
    goToPage(
      RouteName.AlbumScreen,
      { id: vaultId, albumId: album.id },
      {},
      origin,
    );
  };

  if (allAlbums.length === 0 && !scoped.canCreateAlbumHere) {
    let emptyText = 'No albums have been shared with you yet.';
    if (scoped.loading) emptyText = 'Loading shared albums…';
    else if (scoped.othersOnly)
      emptyText = 'No one else has created albums in this vault yet.';
    return (
      <div className={styles.albumsContainer}>
        <p className={styles.empty}>{emptyText}</p>
      </div>
    );
  }
  if (allAlbums.length === 0 && !scoped.inOthersVault) {
    return (
      <div className={styles.albumsContainer}>
        <EmptyDashboardState />
      </div>
    );
  }

  return (
    <div className={styles.albumsContainer}>
      <div className={styles.albumsSection}>
        {filteredAlbums.length > 0 || scoped.canCreateAlbumHere ? (
          <div className={styles.albumsGrid}>
            {scoped.canCreateAlbumHere && (
              <NewAlbumCard onClick={() => setShowNewAlbumModal(true)} />
            )}
            {filteredAlbums.map((album) => (
              <CollectionCard
                key={album.id}
                album={album}
                onClick={() => handleAlbumClick(album)}
                sharedBy={
                  scoped.crossVault
                    ? (album as SharedAlbum).sharedBy
                    : undefined
                }
              />
            ))}
          </div>
        ) : (
          <EmptyAlbumsState />
        )}
      </div>

      {showNewAlbumModal && (
        <NewAlbumModal
          onSubmit={handleCreateNamedAlbum}
          onClose={() => setShowNewAlbumModal(false)}
        />
      )}
    </div>
  );
};
